import axios from 'axios';
import { Storage } from "@/services/Storage";
import store from '@/store';

// Base URL: window.lytstoreApi (set in GlobalVariables.js)
// No authentication required per spec

const base = () => {
  if (!window.lytstoreApi) {
    throw new Error('lytstoreApi is not configured on window');
  }
  return window.lytstoreApi;
};

// Helpers to build urls
const url = {
  pending: (storeDB) => `${base()}/${encodeURIComponent(storeDB)}/orders/pending`,
  list: (storeDB) => `${base()}/${encodeURIComponent(storeDB)}/orders`,
  get: (storeDB, orderId) => `${base()}/${encodeURIComponent(storeDB)}/orders/${encodeURIComponent(orderId)}`,
  getSale: (storeDB, saleNum) => `${base()}/${encodeURIComponent(storeDB)}/orders/sale/${encodeURIComponent(saleNum)}`,
  accept: (storeDB, orderId) => `${base()}/${encodeURIComponent(storeDB)}/orders/${encodeURIComponent(orderId)}/accept`,
  reject: (storeDB, orderId) => `${base()}/${encodeURIComponent(storeDB)}/orders/${encodeURIComponent(orderId)}/reject`,
  adjust: (storeDB, orderId) => `${base()}/${encodeURIComponent(storeDB)}/orders/${encodeURIComponent(orderId)}/items/adjust`,
  updateStatus: (storeDB, orderId) => `${base()}/${encodeURIComponent(storeDB)}/orders/${encodeURIComponent(orderId)}/status`,
  registration: (storeDB) => `${base()}/${encodeURIComponent(storeDB)}/lytconnect/registration`,
};

const api = {
  async getPending(storeDB) {
    const { data } = await axios.get(url.pending(storeDB), { timeout: 8000 });
    return data;
  },
  async getOrders(storeDB, params = {}) {
    const { data } = await axios.get(url.list(storeDB), { params, timeout: 10000 });
    return data;
  },
  async getOrder(storeDB, orderId) {
    const { data } = await axios.get(url.get(storeDB, orderId), { timeout: 10000 });
    return data;
  },
  async getSale(storeDB, saleNum) {
    const { data } = await axios.get(url.getSale(storeDB, saleNum), { timeout: 10000 });
    return data;
  },
  async checkRegistration(storeDB) {
    const { data } = await axios.get(url.registration(storeDB), { timeout: 8000 });
    return data;
  },
  async acceptOrder(storeDB, orderId, saleNum, reason) {
    const payload = {
      saleNumber: String(saleNum),
      reason: reason
    };
    const { data } = await axios.post(url.accept(storeDB, orderId), payload);
    return data;
  },
  async rejectOrder(storeDB, orderId, reason) {
    const payload = { reason };
    const { data } = await axios.post(url.reject(storeDB, orderId), payload);
    return data;
  },
  async adjustItems(storeDB, orderId, payload) {
    // payload per spec: { items: [{ itemId, action, newQuantity, itemReason }], reason }
    const { data } = await axios.post(url.adjust(storeDB, orderId), payload);
    return data;
  },
  async updateOrderStatus(storeDB, orderId, status, notes) {
    const payload = { status, notes };
    const { data } = await axios.put(url.updateStatus(storeDB, orderId), payload);
    return data;
  },

  async acceptLytStoreOrderAndCreateTable(storeDB, order) {
    // The 'order' parameter is the order data object itself.
    // No need to access a .data property.
    if (!order || !Array.isArray(order.items) || order.items.length === 0) {
      throw new Error('Online order has no items to create a table from.');
    }

    // 1. Get current user and device settings
    const user = store.getters.getCurrentUser;
    if (!user || !user.Username) {
      throw new Error('Could not determine current logged-in user.');
    }
    const settings = await Storage.getDeviceSettings();

    // 2. Transform LytStore order items into the sale format
    const saleItems = order.items.map((item, index) => {
      const qty = Number(item.quantity);
      const unit = Number(item.unitPrice);
      // Assuming no item-level discount is provided in the LytStore order item
      const discount = 0;
      const lineTotal = (qty * unit) - discount;

      let instructions = [];
      if (item.specialInstructions) {
        try {
          // Safely parse the JSON string for special instructions
          instructions = JSON.parse(item.specialInstructions);
          if (!Array.isArray(instructions)) {
            instructions = [];
          }
        } catch (e) {
          console.error('Failed to parse specialInstructions JSON:', e);
          instructions = [];
        }
      }

      return {
        Plu: item.productPlu,
        Description: item.productName,
        Qty: qty,
        Unit: unit,
        Discount: discount,
        Line: lineTotal,
        instructions: instructions,
        saleLine: index + 1,
      };
    });

    // 3. Construct the payload for the 'tables' endpoint
    const createTablePayload = {
      SaleNum: 0, // 0 indicates a new table/sale
      Name: `shop.co.zm Order #${order.orderNumber}`,
      User: user.Username,
      PC: settings.PC,
      items: saleItems,
      Customer: order.payment?.debtorId || '',
      discount: Number(order.pricing?.subtotal || 0) - Number(order.pricing?.totalAmount || 0),
    };

    // 4. First API Call: Create the sale/table in the POS backend
    const createTableResponse = await axios.post(
      window.serverApi + `tables/${storeDB}`,
      createTablePayload
    );

    // 5. Extract the SaleNum and Name from the response
    const createdTable = createTableResponse.data || {};
    const saleNum = createdTable.SaleNum ?? createdTable.saleNum ?? createdTable.saleNumber;
    const tableName = createdTable.Name || createTablePayload.Name;

    if (!saleNum) {
      throw new Error('Failed to create table or retrieve SaleNum from the backend.');
    }

    // 6. Second API Call: Accept the order in LytStore, providing the new SaleNum and Name as reason
    const acceptance = await api.acceptOrder(storeDB, order.id, saleNum, tableName);

    return {
      saleNumber: saleNum,
      tableName,
      table: createdTable,
      acceptance,
    };
  }
};

export default api;

